import { $window, $body } from '../../../Constants';
import { Settings } from '../Settings';
import HtmlElement from '../../../Elements/HtmlElement';
import Slide from './Slide';
import BannerCta from '../../../Elements/BannerCta';

/**
 * HeaderSlider
 */
const HeaderSlider = (() => {
  // Constants
  const DATA_KEY = 'ig.headerslider';
  const EVENT_KEY = `.${DATA_KEY}`;

  /**
   * Selector
   *
   * @type { Object.<String> }
   */
  const Selector = {
    SLIDER: '.tx-ig-headerslider',
    STAGE: '.headerslider-stage',
    CONTENTS: '.headerslider-contents',
    CONTENT: '.headerslider-content',
    NAV_DOTS: '.headerslider-dots',
    NAV_DOT: '.headerslider-dot'
  };
  /**
   * Event
   */
  const Event = {
    RESIZE: `resize${EVENT_KEY}`
  };


  /**
   * HeaderSlider
   *
   * @class
   */
  class HeaderSlider extends HtmlElement {
    // Public

    /**
     * Handles ready event
     *
     * @return void
     */
    onReady() {
      this._$stage = this.$element.find(Selector.STAGE);
      this._$dotsNav = this.$element.find(Selector.NAV_DOTS);
      this._$dotsNavDots = this._$dotsNav.find(Selector.NAV_DOT);
      this._index = 0;
      this._dimsRatio = Settings.height / Settings.width;
      this._isAnimating = false;
      this._timeout = null;
      this._animationFrame = null;

      this.$contents = this.$element.find(Selector.CONTENTS);
      this.$content = this.$element.find(Selector.CONTENT);
      this.$activeContent = null;

      this._slides = this._getSlides();
      this._length = this._slides.length;

      this._initialize();
    }

    /**
     * Sets the height of the content
     *
     * @return void
     */
    setContentHeightAndPosition(height = 0) {
      this.$content.css('height', height);

      if ($window.width() >= Settings.breakpoint) {
        this.$content.css('margin-top', -Math.ceil(height / 2));
      } else {
        this.$content.css('margin-top', '');
      }
    }

    // Private

    /**
     * Initialize
     *
     * @return void
     */
    _initialize() {
      this._setStageHeight();

      let firstSlide = this._getSlideByIndex(this._index);

	  if (!firstSlide) {
		  return;
	  }

      firstSlide.show();

      this._bindHandlers();

      if (this._$dotsNav.length) {
        this._initializeDotsNav();
      }

      if (this._length > 1) {
        this._start();
      }
    }

    /**
     * Initializes the dots nav
     *
     * @return void
     */
    _initializeDotsNav() {
      this._changeActiveNavDot(this._$dotsNavDots.eq(this._index));

      this._$dotsNavDots.stop(true, false).on('click', (e) => {
        let $dot = $(e.currentTarget);

        if (!$dot.hasClass('active')) {
          e.preventDefault();

          let slide = this._getSlideByIndex($dot.index());

          if (slide) {
            this._changeToSlide(slide);
          }
        }
      });

      this._adjustDotsNavPosition();
    }

    /**
     * Changes the active nav dot
     *
     * @return void
     */
    _changeActiveNavDot($dot) {
      this._$dotsNavDots.removeClass('active');
      $dot.addClass('active');
    }

    /**
     * Adjusts the position of the dots nav
     *
     * @return void
     */
    _adjustDotsNavPosition() {
      if (BannerCta.exists()) {
        let margin = 0;

        if ($window.width() >= BannerCta.constructor.MOBILE_BREAKPOINT) {
          margin = BannerCta.getHeight() / 2;
        } else {
          margin = 0;
        }

        this._$dotsNav.css('margin-bottom', margin);
      }
    }

    /**
     * Binds handlers to the elements
     *
     * @return void
     */
    _bindHandlers() {
      $window.on(Event.RESIZE, () => {
        this._setStageHeight();

        if (this.$activeContent) {
          this.setContentHeightAndPosition(this.$activeContent.outerHeight());
        }

        if (this._$dotsNav.length) {
          this._adjustDotsNavPosition();
        }
      });

      if (Settings.pauseOnHover === 1 && this._length > 1) {
        this.$element.on('mouseenter', () => {
          this._pause();
        });

        this.$element.on('mouseleave', () => {
          this._start();
        });
      }
    }

    /**
     * Returns all available slides
     *
     * @return { Array } slides
     */
    _getSlides() {
      let slides = [];

      this._$stage.find(Slide.Selector.IMAGE).each((i, el) => {
        slides.push(new Slide($(el), this));
      });

      return slides;
    }

    /**
     * Returns the next slide
     *
     * @return { Slide }
     */
    _getPrevSlide() {
      let prev = this._length - 1;

      if (this._index - 1 >= 0) {
        prev = this._index - 1;
      }

      return this._getSlideByIndex(prev);
    }

    /**
     * Returns the previous slide
     *
     * @return { Slide }
     */
    _getNextSlide() {
      let next = 0;

      if (this._index + 1 < this._length) {
        next = this._index + 1;
      }

      return this._getSlideByIndex(next);
    }

    /**
     * Returns the slide with the given index
     *
     * @param { int } index
     * @return { Slide }
     */
    _getSlideByIndex(index) {
      return this._slides[index];
    }

    /**
     * Changes the current slide to given one
     *
     * @param { Slide } slide
     */
    _changeToSlide(slide) {
      if (!this._isAnimating) {
        this._isAnimating = true;
        let current = this._getSlideByIndex(this._index);

        current.hide();
        slide.show(() => {
          this._isAnimating = false;
        });

        // Set new index
        this._index = this._slides.indexOf(slide);

        if (this._$dotsNav.length) {
          this._changeActiveNavDot(this._$dotsNavDots.eq(this._index));
        }
      }
    }

    /**
     * Sets the height of the stage
     *
     * @return void
     */
    _setStageHeight() {
      this._$stage.css('height', this._dimsRatio * $window.width());
    }

    /**
     * Automatically changes the current slide to the next one
     *
     * @return void
     */
    _changeSlide() {
      this._timeout = setTimeout(() => {
        this._changeToSlide(this._getNextSlide());
        window.requestAnimationFrame(() => {
          this._changeSlide();
        });
      }, Settings.timeout);
    }

    /**
     * Starts the sliding
     *
     * @return void
     */
    _start() {
      this._animationFrame = window.requestAnimationFrame(() => {
        this._changeSlide();
      });
    }

    /**
     * Pauses the sliding
     *
     * @return void
     */
    _pause() {
      cancelAnimationFrame(this._animationFrame);
      clearTimeout(this._timeout);
    }
  }

  return HeaderSlider._getElement($body.find(Selector.SLIDER), HeaderSlider);
})();

export default HeaderSlider;
